/**
 * Chat markdown. Text is escaped before any tag is added, so model and user
 * HTML stays inert. Chart ids and the tag set match public/chat.js — the
 * snapshot in test/markdown-render.test.js must stay byte-identical.
 */
import { mountCharts } from './markdown-chart.js';
import {
  sameOriginUploadUrl,
  bindProtectedUploadClicks,
  hydrateProtectedMedia,
} from './upload-download.js';
import { bindEvidenceChips, evidenceChipHtml } from './evidence-popover.js';

export {
  sameOriginUploadUrl,
  needsAuthedUpload,
  downloadProtectedUpload,
  fetchProtectedUploadObjectUrl,
  bindProtectedUploadClicks,
  hydrateProtectedMedia,
} from './upload-download.js';
export { bindEvidenceChips } from './evidence-popover.js';

let chartSeq = 0;

export function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function stripInlineMarkup(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&');
}

function chartContainer(type, payload) {
  chartSeq += 1;
  const id = `chart-${chartSeq}-${Math.random().toString(36).slice(2, 7)}`;
  const json = escapeHtml(JSON.stringify(payload));
  const height = (type === 'pie' || type === 'doughnut' || type === 'funnel') ? 280 : 250;
  return `<div class="msg-chart" id="${id}" data-chart-type="${type}" data-chart-json="${json}" style="height:${height}px"></div>`;
}

function autoChartFromTable(headerCells, bodyRows) {
  if (!bodyRows || bodyRows.length < 2 || headerCells.length < 2) return null;
  const labels = bodyRows.map((row) => stripInlineMarkup(row[0])).filter((item) => item !== '');
  if (labels.length < 2) return null;
  const numericLabels = labels.map((label) => Number(label));
  const isSequentialIndex = numericLabels.every((n) => Number.isInteger(n))
    && numericLabels.every((n, i) => n === i + 1);
  if (isSequentialIndex) return null;

  const series = [];
  for (let col = 1; col < headerCells.length; col += 1) {
    let allNumeric = true;
    let hasAny = false;
    const data = [];
    for (let row = 0; row < bodyRows.length; row += 1) {
      const raw = (bodyRows[row][col] || '').replace(/[,%$£€\s]/g, '');
      if (raw === '' || raw === '-' || raw === '—' || raw === 'n/a') {
        data.push(null);
        continue;
      }
      if (!/^-?\d*\.?\d+$/.test(raw)) {
        allNumeric = false;
        break;
      }
      hasAny = true;
      data.push(parseFloat(raw));
    }
    if (allNumeric && hasAny) series.push({ label: stripInlineMarkup(headerCells[col]), data });
  }
  if (!series.length) return null;
  const looksTime = /(\d{4}|\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)|[-/]\d{1,2}[-/])/i.test(labels.join(' '));
  const cappedLabels = labels.slice(0, 30);
  const cappedSeries = series.map((item) => ({ label: item.label, data: item.data.slice(0, 30) }));
  return chartContainer(looksTime ? 'line' : 'bar', { labels: cappedLabels, series: cappedSeries });
}

function takeJsonObject(text, from) {
  const start = String(text || '').indexOf('{', from);
  if (start < 0) return null;
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (inStr) {
      if (esc) { esc = false; continue; }
      if (ch === '\\') { esc = true; continue; }
      if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') { inStr = true; continue; }
    if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) return { json: text.slice(start, i + 1), end: i + 1 };
    }
  }
  return null;
}

export function extractChartBlocks(rawText) {
  const charts = [];
  const src = String(rawText || '');
  const parts = [];
  let i = 0;
  const fenceRe = /```chart(?::([a-zA-Z-]+))?/gi;
  const bareRe = /(^|\n)[ \t]*chart:(bar|line|pie|doughnut|funnel|hbar)\b/gi;

  while (i < src.length) {
    fenceRe.lastIndex = i;
    bareRe.lastIndex = i;
    const fence = fenceRe.exec(src);
    const bare = bareRe.exec(src);
    let match = null;
    let kind = null;
    let type = 'auto';
    let after = 0;
    if (fence && (!bare || fence.index <= bare.index)) {
      match = fence;
      kind = 'fence';
      type = (fence[1] || 'auto').toLowerCase();
      after = fence.index + fence[0].length;
    } else if (bare) {
      match = bare;
      kind = 'bare';
      type = (bare[2] || 'auto').toLowerCase();
      after = bare.index + bare[0].length;
    }
    if (!match) {
      parts.push(src.slice(i));
      break;
    }
    parts.push(src.slice(i, match.index));
    if (kind === 'bare' && match[1]) parts.push(match[1]);
    const taken = takeJsonObject(src, after);
    if (!taken) {
      parts.push(src.slice(match.index + (kind === 'bare' ? (match[1] || '').length : 0), after));
      i = after;
      continue;
    }
    let payload = {};
    try { payload = JSON.parse(taken.json); } catch { payload = {}; }
    if (!payload || !Object.keys(payload).length) {
      parts.push(src.slice(match.index + (kind === 'bare' ? (match[1] || '').length : 0), taken.end));
      i = taken.end;
      continue;
    }
    charts.push({ type, payload });
    parts.push(`CHARTBLOCK_${charts.length - 1}_CHARTBLOCK`);
    i = taken.end;
    const close = src.slice(i).match(/^\s*```/);
    if (close) i += close[0].length;
  }
  return { html: parts.join(''), charts };
}

function convertMarkdownTables(text) {
  const lines = text.split('\n');
  const isRow = (line) => /^\|.*\|\s*$/.test(line.trim());
  const isSeparator = (line) => /^\|(\s*:?-{1,}:?\s*\|)+\s*$/.test(line.trim());
  const splitCells = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
  const out = [];
  let i = 0;
  while (i < lines.length) {
    if (isRow(lines[i]) && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      const headerCells = splitCells(lines[i]);
      i += 2;
      const bodyRows = [];
      while (i < lines.length && isRow(lines[i])) {
        bodyRows.push(splitCells(lines[i]));
        i += 1;
      }
      const thead = `<tr>${headerCells.map((cell) => `<th>${cell}</th>`).join('')}</tr>`;
      const tbody = bodyRows.map((cells) => `<tr>${cells.map((cell) => `<td>${cell}</td>`).join('')}</tr>`).join('');
      const tableHtml = `<div class="msg-table-wrap"><table class="msg-table"><thead>${thead}</thead><tbody>${tbody}</tbody></table></div>`;
      out.push((autoChartFromTable(headerCells, bodyRows) || '') + tableHtml);
    } else {
      out.push(lines[i]);
      i += 1;
    }
  }
  return out.join('\n');
}

/**
 * @param {string} rawText
 * @param {{ evidence?: Record<string, object> }} [opts]
 */
export function renderAgentMarkdown(rawText, opts) {
  const evidence = (opts && opts.evidence) || {};
  const { html: chartStripped, charts } = extractChartBlocks(rawText);
  let html = escapeHtml(chartStripped);
  html = html.replace(/```(?:\w+\n)?([\s\S]*?)```/g, (_, code) => `<pre class="msg-code-block"><code>${code.trim()}</code></pre>`);
  html = html.replace(/`([^`\n]+)`/g, '<code class="msg-inline-code">$1</code>');
  html = html.replace(/^### (.+)$/gm, '<h4 class="msg-h4">$1</h4>');
  html = html.replace(/^## (.+)$/gm, '<h3 class="msg-h3">$1</h3>');
  html = html.replace(/^# (.+)$/gm, '<h2 class="msg-h2">$1</h2>');
  html = html.replace(/^---+$/gm, '<hr class="msg-hr">');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\[([^\]]+)\]\((\/uploads\/[^\s)]+|https?:\/\/[^\s)]+)\)/g, (m, label, href) => {
    const url = sameOriginUploadUrl(href);
    return `<a class="msg-download-link" href="${url}" target="_blank" rel="noopener" download>${label}</a>`;
  });
  html = html.replace(/(^|[\s(])(\/uploads\/[A-Za-z0-9._~!$&'()*+,;=:@\/\-]+)/g, (m, pre, p) => {
    const url = p.replace(/[.,;:!?]+$/, '');
    const label = url.split('/').pop() || url;
    return `${pre}<a class="msg-download-link" href="${url}" target="_blank" rel="noopener" download>📄 Download: ${label}</a>`;
  });
  html = html.replace(/(^|[\s(])(https?:\/\/[^\s<>"']+)/g, (m, pre, u) => {
    const raw = u.replace(/[.,;:!?]+$/, '');
    const isUpload = /\/uploads\//.test(raw);
    const url = isUpload ? sameOriginUploadUrl(raw) : raw;
    const label = isUpload ? (`📄 Download: ${url.split('/').pop() || url}`) : raw;
    return `${pre}<a class="msg-download-link" href="${url}" target="_blank" rel="noopener"${isUpload ? ' download' : ''}>${label}</a>`;
  });
  html = convertMarkdownTables(html);
  html = html.replace(/(?:^|\n)((?:- .+(?:\n|$))+)/g, (block) => {
    const items = block.trim().split('\n').map((line) => `<li>${line.replace(/^- /, '')}</li>`).join('');
    return `\n<ul class="msg-list">${items}</ul>`;
  });
  html = html.replace(/^(\d+)\. (.+)$/gm, (_, num, text) => `<ol class="msg-list" start="${num}"><li>${text}</li></ol>`);
  // Evidence chips after markdown so tokens in prose become buttons; unknown refs stay text.
  html = html.replace(/\[\[ev:(F\d+)\]\]/g, (_, ref) => evidenceChipHtml(ref, evidence[ref], escapeHtml));
  html = html.replace(/\n/g, '<br>');
  html = html.replace(/CHARTBLOCK_(\d+)_CHARTBLOCK/g, (_, index) => {
    const chart = charts[+index];
    return (chart && Object.keys(chart.payload).length) ? chartContainer(chart.type, chart.payload) : '';
  });
  return html;
}

/**
 * Put renderer output into a node. Raw text is never assigned as HTML.
 * @param {HTMLElement} node
 * @param {string} raw
 * @param {{
 *   evidence?: Record<string, object>,
 *   runId?: string,
 *   isAdmin?: boolean,
 *   apiGet?: Function,
 * }} [opts]
 */
export function fillMarkdown(node, raw, opts) {
  const tpl = document.createElement('template');
  tpl.innerHTML = renderAgentMarkdown(raw, opts);
  node.replaceChildren(tpl.content);
  bindProtectedUploadClicks(node);
  hydrateProtectedMedia(node);
  bindEvidenceChips(node, opts);
  // The node is often not in the document yet. ECharts sizes from clientWidth,
  // which is 0 until layout, so wait a frame.
  requestAnimationFrame(() => mountCharts(node));
}
